// ============================================================================
// Recording a creator payout
// ============================================================================
// The single write that settles a creator payout, shared by the two places ops
// can trigger one: the Creator Payments table (dialog) and the hire detail page
// (inline panel).
//
// Shared deliberately. A refund can safely be written twice in the UI because
// the money moves inside wallet_refund_hire_to_credit — the DB owns the payload
// and re-checks the rules. A payout has no such backstop: it's a plain UPDATE on
// cr_hires, so the payload, the eligibility check and the audit description are
// the whole of the operation. Two copies would eventually disagree about one of
// them (a missing paid_by, a differently-worded audit trail), and money writes
// are the worst place to discover that.
//
// Eligibility lives in payouts.ts, which stays free of imports so it can be
// unit-tested; this module is the side-effecting half.
// ============================================================================

import { supabase } from './supabase'
import { logUpdate } from './audit-log'
import {
  payoutState, payoutSubmissionCounts, type PayableHire, type PayoutMode,
} from './payouts'
import { usd } from './refunds'

export interface RecordPaymentInput {
  transactionId: string
  amountCents: number
  notes?: string
  /**
   * Videos counting toward the requirement — submitted, not admin-rejected, not
   * deleted. Passed in rather than queried because both callers already have it,
   * and because it must be the SAME count the refund used.
   */
  deliveredVideos: number
}

export interface RecordPaymentResult {
  mode: PayoutMode | null
  amountCents: number
  deliveredVideos: number
  requiredVideos: number
}

/**
 * Marks a hire paid. Throws with an ops-readable message if the hire isn't
 * payable, or if the transaction ID / amount is missing — both are required, and
 * a payout with no transaction ID can't be reconciled against the bank later.
 */
export async function recordHirePayment(
  hire: PayableHire & { id: string },
  input: RecordPaymentInput,
): Promise<RecordPaymentResult> {
  // Re-checked here, not trusted from the caller's button state: the row may have
  // gone stale while a dialog or panel sat open. Two gates run at write time:
  //
  //   1. the video-approval state is RE-READ from the database — never taken from
  //      the caller's in-memory copy — because a payout over an unreviewed video
  //      means money moved on work nobody signed off on. A hire with any counting
  //      submission still awaiting approval is not payable no matter what the UI
  //      showed.
  //   2. the whole payoutState() rule set (delivery, refund, deadline, ...), now
  //      including gate 1, is re-evaluated against that fresh state.
  const { data: subs, error: subsErr } = await supabase
    .from('cr_video_submissions')
    .select('review_status, deleted')
    .eq('hire_id', hire.id)
  if (subsErr) throw subsErr

  const { pending: pendingVideos } = payoutSubmissionCounts((subs ?? []) as { review_status: 'approved' | 'rejected' | null; deleted: boolean }[])
  const payout = payoutState(hire, input.deliveredVideos, pendingVideos)
  if (!payout.eligible) {
    throw new Error(`Cannot pay: ${payout.blockedReason ?? 'this hire is not payable'}.`)
  }

  const transactionId = input.transactionId.trim()
  if (!transactionId) throw new Error('A transaction ID is required to record a payment.')
  if (!(input.amountCents > 0)) throw new Error('Enter a payment amount greater than zero.')

  const { data: { session } } = await supabase.auth.getSession()
  const adminEmail = session?.user?.email ?? null
  const now = new Date().toISOString()

  const payload = {
    payment_status: 'paid' as const,
    payment_transaction_id: transactionId,
    payment_amount_cents: input.amountCents,
    paid_at: now,
    paid_by: adminEmail,
    payment_notes: input.notes?.trim() || null,
  }

  // These six columns are exactly what the admin's UPDATE grant on cr_hires covers.
  // The grant is column-scoped on purpose — creators hold row-level UPDATE policies
  // on their own hires, so the authenticated role must stay narrow — which means
  // this client CANNOT close the engagement itself. It used to try, stamping
  // settled_at in a second statement, and got 42501 "permission denied for table
  // cr_hires" AFTER the payment had already gone through: the creator was paid and
  // the UI reported a failure.
  //
  // Closing now happens in the database, on either half of the settlement (refund
  // or payout), in cr_hires_close_engagement() — a SECURITY DEFINER trigger, so it
  // needs no grant and cannot be skipped by a caller that forgets. See
  // 20260807040000_close_hire_engagement_trigger.sql.
  const { error } = await supabase.from('cr_hires').update(payload).eq('id', hire.id)
  if (error) throw error

  // The audit trail is the only place a payout's SHAPE is recorded — the row just
  // holds an amount, and "$60 on a $100 hire" doesn't say whether that was a
  // deliberate partial payout or a typo.
  const shape = payout.mode === 'partial'
    ? `partial payout for ${payout.deliveredVideos} of ${payout.requiredVideos} videos (earned ${usd(payout.earnedCents)}, withheld ${usd(payout.withheldCents)}) — engagement closed, creator can be re-hired on this campaign`
    : 'full payout'
  await logUpdate('cr_hires', hire.id, { payment_status: hire.payment_status }, payload, {
    description: `Admin recorded ${shape} — tx: ${transactionId}`,
  })

  return {
    mode: payout.mode,
    amountCents: input.amountCents,
    deliveredVideos: payout.deliveredVideos,
    requiredVideos: payout.requiredVideos,
  }
}
